import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TableContainer,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import CustomTooltip from "../../components/CustomTooltip";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import AssignEngineerModal from "../../components/Inhouse/AssignEngineerModal";
import toast from "react-hot-toast";
import ConfirmDialog from "../../components/ui/ConfirmDialog";

const statusStyles = {
  "On Track": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "Report Missing": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  "ONTRACK": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "REPORTMISSING": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  "ACTIVE": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "COMPLETED": {
    bgcolor: "#4CAF50",
    color: "#ffffff",
  },
  "ON_HOLD": {
    bgcolor: "#FF9800",
    color: "#ffffff",
  },
  "CANCELLED": {
    bgcolor: "#F44336",
    color: "#ffffff",
  },
};

// Fixed headers
const headers = [
  { key: "Project Name", sortable: true, field: "projectName" },
  { key: "Start Date", sortable: true, field: "projectStartDate" },
  { key: "End Date", sortable: true, field: "projectEndDate" },
  { key: "Engineer", sortable: false, field: "assignedEngineerName" },
  { key: "DPR", sortable: false, field: "DPR" },
  { key: "WPR", sortable: false, field: "WPR" },
  { key: "MPR", sortable: false, field: "MPR" },
  { key: "Status", sortable: false, field: "status" },
  { key: "Actions", sortable: false },
];

const InHouseProjects = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Get auth context - use userData instead of user
  const { token, userData, loading: authLoading, isEngineer, isAdmin, isSuperAdmin } = useAuth() || {};

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Check user role from userData
  const isEngineerRole = userData?.role === "ENGINEER";
  const isAdminOrSuperAdmin = userData?.role === "ADMIN" || userData?.role === "SUPERADMIN";

  // State for data and loading
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [pageSize] = useState(10);

  // State for UI controls
  const [viewAnchorEl, setViewAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);

  // Sort and filter states
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSort = localStorage.getItem("inhouseProjectsSort");
    return savedSort ? JSON.parse(savedSort) : {
      field: "createdAt",
      direction: "desc"
    };
  });

  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("inhouseProjectsFilters");
    return savedFilters ? JSON.parse(savedFilters) : {
      search: "",
      status: "",
      dateFrom: "",
      dateTo: "",
    };
  });

  // Modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Save sort config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("inhouseProjectsSort", JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("inhouseProjectsFilters", JSON.stringify(filters));
  }, [filters]);

  // Fetch projects from backend - ROLE-BASED API CALL
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page,
        limit: pageSize,
        search: filters.search || undefined,
        status: filters.status || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      };

      // Add sort parameters based on sortConfig
      let sortParam = "latest";
      if (sortConfig.field === "projectName") {
        sortParam = sortConfig.direction === "asc" ? "nameAsc" : "nameDesc";
      } else if (sortConfig.field === "projectStartDate") {
        sortParam = sortConfig.direction === "asc" ? "startDateAsc" : "startDateDesc";
      } else if (sortConfig.field === "projectEndDate") {
        sortParam = sortConfig.direction === "asc" ? "startDateAsc" : "startDateDesc";
      } else if (sortConfig.field === "createdAt") {
        sortParam = sortConfig.direction === "asc" ? "oldest" : "latest";
      }

      params.sort = sortParam;

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: params,
      };

      let response;

      // ROLE-BASED API CALL - FIX: Use userData._id
      if (isEngineerRole && userData?._id) {
        // Use engineer-specific API
        response = await axios.get(`${url}/api/inhouse-projects/engineer/${userData._id}`, config);

        // Engineer's projects are in response.data.projects
        if (response.data.projects) {
          setProjects(response.data.projects);
          setTotalProjects(response.data.total || response.data.projects.length);
          setTotalPages(Math.ceil((response.data.total || response.data.projects.length) / pageSize));
        }
      } else {
        // Use general API for Admin/SuperAdmin
        response = await axios.get(`${url}/api/inhouse-projects/fetch`, config);

        if (response.data.projects) {
          setProjects(response.data.projects);
          setTotalProjects(response.data.total || response.data.projects.length);
          setTotalPages(Math.ceil((response.data.total || response.data.projects.length) / pageSize));
        }
      }

    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to fetch projects data");
      showSnackbar("Failed to fetch projects data. Please try again.", "error");
      setProjects([]);
      setTotalProjects(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, token, sortConfig, filters, isEngineerRole, userData?._id]);

  // Initial fetch - only when auth is loaded and user data is available
  useEffect(() => {
    if (!authLoading && userData) {
      fetchProjects();
    }
  }, [fetchProjects, authLoading, userData]);

  // Check if any filter is applied
  const isFilterApplied = () => {
    return (
      filters.search !== "" ||
      filters.status !== "" ||
      filters.dateFrom !== "" ||
      filters.dateTo !== "" ||
      sortConfig.field !== "createdAt" ||
      sortConfig.direction !== "desc"
    );
  };

  // Handle assign engineer button click - Only show if admin/superadmin and no engineer assigned
  const handleAssignEngineer = (project) => {
    if (isAdminOrSuperAdmin && !project.assignedEngineer && !project.assignedEngineerName) {
      setSelectedProject(project);
      setAssignModalOpen(true);
    }
  };

  // Handle successful assignment
  const handleAssignmentSuccess = (updatedProject) => {
    // Update the project in the list
    setProjects(prevProjects =>
      prevProjects.map(project =>
        project._id === updatedProject._id ? updatedProject : project
      )
    );

    // Refetch the projects to get the latest data
    fetchProjects();
  };

  // Handle sorting
  const handleSort = (field) => {
    const newSortConfig = {
      field,
      direction: sortConfig.field === field && sortConfig.direction === "asc" ? "desc" : "asc"
    };
    setSortConfig(newSortConfig);
    setPage(1); // Reset to first page when sorting
  };

  // Get sort icon for a specific field
  const getSortIcon = (field) => {
    if (sortConfig.field === field) {
      return sortConfig.direction === "asc" ? "foldUp" : "foldDown";
    }
    return "sort";
  };

  // Handle global sort selection
  const handleGlobalSort = (sortType) => {
    let field, direction;

    switch (sortType) {
      case "LATEST":
        field = "createdAt";
        direction = "desc";
        break;
      case "OLDEST":
        field = "createdAt";
        direction = "asc";
        break;
      case "AZ":
        field = "projectName";
        direction = "asc";
        break;
      case "ZA":
        field = "projectName";
        direction = "desc";
        break;
      default:
        field = "createdAt";
        direction = "desc";
    }

    setSortConfig({ field, direction });
    setSortAnchorEl(null);
    setPage(1);
  };

  // Handle view filter selection
  const handleViewFilter = (viewType) => {
    const newFilters = {
      ...filters,
      status: viewType === "ALL" ? "" : viewType
    };
    setFilters(newFilters);
    setViewAnchorEl(null);
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      dateFrom: "",
      dateTo: "",
    });
    setSortConfig({
      field: "createdAt",
      direction: "desc"
    });
    setPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get status display text
  const getStatusText = (status) => {
    switch (status) {
      case "ACTIVE":
      case "ONTRACK":
        return "On Track";
      case "COMPLETED":
        return "Completed";
      case "ON_HOLD":
        return "On Hold";
      case "CANCELLED":
        return "Cancelled";
      case "REPORTMISSING":
        return "Report Missing";
      default:
        return status || "On Track";
    }
  };

  // Handle delete project - Only for admin/superadmin
  const handleDeleteProject = async (projectId, projectName) => {
    setProjectToDelete({ id: projectId, name: projectName });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;
    
    try {
      const response = await axios.delete(
        `${url}/api/inhouse-projects/delete/${projectToDelete.id}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (response.status === 200) {
        toast.success(`Project "${projectToDelete.name}" deleted successfully!`);
        fetchProjects(); // Refresh the list
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project. Please try again.");
    } finally {
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
    }
  };

  // Navigation handlers
  const handleAddProjectClick = () => {
    navigate("/progress/add-inhouse-project");
  };

  const handleEditProject = (projectId) => {
    navigate(`/progress/edit-report/${projectId}`);
  };

  // Snackbar functions
  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Responsive table headers
  const getVisibleHeaders = () => {
    if (isMobile) {
      return headers.filter(header =>
        ["Project Name", "Engineer", "Status", "Actions"].includes(header.key)
      );
    }
    if (isTablet) {
      return headers.filter(header =>
        !["DPR", "WPR", "MPR"].includes(header.key)
      );
    }
    return headers;
  };

  // Check if project has engineer assigned
  const hasEngineerAssigned = (project) => {
    return project.assignedEngineer || project.assignedEngineerName;
  };

  // Responsive column rendering
  const renderTableCell = (header, project) => {
    const getFieldValue = (field) => {
      switch (field) {
        case "projectName":
          return project.projectName;
        case "projectStartDate":
          return formatDate(project.projectStartDate);
        case "projectEndDate":
          return formatDate(project.projectEndDate);
        case "assignedEngineerName":
          return project.assignedEngineerName || project.assignedEngineer?.name || "-";
        case "DPR":
          return String(project.DPR || 0).padStart(2, "0");
        case "WPR":
          return String(project.WPR || 0).padStart(2, "0");
        case "MPR":
          return String(project.MPR || 0).padStart(2, "0");
        case "status":
          return getStatusText(project.status);
        default:
          return project[field] || "-";
      }
    };

    switch (header.key) {
      case "Project Name":
        return (
          <TableCell key={header.key}>
            <Box>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                {getFieldValue("projectName")}
              </Typography>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.MUTED_TEXT,
                }}
              >
                Developer: {project.developerName || "-"}
              </Typography>
            </Box>
          </TableCell>
        );

      case "Start Date":
      case "End Date":
      case "Engineer":
      case "DPR":
      case "WPR":
      case "MPR":
        return (
          <TableCell key={header.key} sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE }}>
            {getFieldValue(header.field)}
          </TableCell>
        );

      case "Status":
        const statusText = getStatusText(project.status);
        // Determine which status key to use for styling
        let statusKey = project.status;

        // Handle case-insensitive matching
        if (project.status) {
          const upperStatus = project.status.toUpperCase();
          if (upperStatus === "ONTRACK") {
            statusKey = "ONTRACK";
          } else if (upperStatus === "REPORTMISSING") {
            statusKey = "REPORTMISSING";
          }
        }

        return (
          <TableCell key={header.key}>
            <Chip
              label={statusText}
              sx={{
                // Use the statusKey for styling, fallback to "ACTIVE"
                ...(statusStyles[statusKey] || statusStyles["ACTIVE"]),
                ...TYPOGRAPHY.BODY_SMALL,
                height: 'auto', // Allow height to adjust based on content
                minHeight: 24, // Slightly increased for better readability
                fontWeight: 500,
                borderRadius: 4,
                '& .MuiChip-label': {
                  px: 1.2,
                  py: 0.3, // Reduced vertical padding to fit better
                  whiteSpace: 'normal', // Allow text wrapping
                  textAlign: 'center',
                  lineHeight: 1.3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                maxWidth: '110px', // Control maximum width
                wordBreak: 'break-word', // Break long words if needed
              }}
            />
          </TableCell>
        );

      case "Actions":
        const hasAssignedEngineer = hasEngineerAssigned(project);

        return (
          <TableCell key={header.key}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

              {/* Edit button - Show for all roles */}
              <CustomTooltip title="Edit">
                <IconButton onClick={() => handleEditProject(project._id)}>
                  <Icon name="Action3" color={COLORS.PRIMARY} />
                </IconButton>
              </CustomTooltip>

              {/* Assign Engineer - ONLY for Admin/SuperAdmin when not assigned */}
              {isAdminOrSuperAdmin && !hasAssignedEngineer && (
                <CustomTooltip title="Assign Engineer">
                  <IconButton onClick={() => handleAssignEngineer(project)}>
                    <Icon name="Action5" color="#8CD47E" />
                  </IconButton>
                </CustomTooltip>
              )}

              {/* Delete - ONLY for Admin/SuperAdmin */}
              {isAdminOrSuperAdmin && (
                <CustomTooltip title="Delete">
                  <IconButton
                    onClick={() => handleDeleteProject(project._id, project.projectName)}
                  >
                    <Icon name="Action4" color={COLORS.ERROR} />
                  </IconButton>
                </CustomTooltip>
              )}

            </Box>
          </TableCell>
        );

      default:
        return null;
    }
  };

  // Calculate displayed range for pagination
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalProjects);

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show login prompt if not authenticated
  if (!authLoading && !userData) {
    return (
      <Box sx={{
        p: 3,
        textAlign: 'center'
      }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Please log in to view projects
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          gap: { xs: 2, sm: 2 },
        }}
      >
        <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
          In House Projects {isEngineerRole ? "(My Projects)" : ""}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ml: { sm: "auto" },
            mr: { sm: 2 },
          }}
        >
          {/* Sort Button */}
          <CustomTooltip title="Sort">
            <IconButton onClick={(e) => setSortAnchorEl(e.currentTarget)}>
              <Icon name="sort" color={COLORS.SECONDARY} />
            </IconButton>
          </CustomTooltip>

          {/* View Button */}
          <CustomTooltip title="View">
            <IconButton onClick={(e) => setViewAnchorEl(e.currentTarget)}>
              <Icon name="list" color={COLORS.SECONDARY} />
            </IconButton>
          </CustomTooltip>

          {/* Refresh / Clear */}
          <CustomTooltip title={isFilterApplied() ? "Clear Filters" : "Refresh"}>
            <IconButton
              onClick={isFilterApplied() ? clearFilters : fetchProjects}
            >
              <Icon
                name="rotate"
                color={isFilterApplied() ? COLORS.ERROR : COLORS.SECONDARY}
              />
            </IconButton>
          </CustomTooltip>

          {/* Add Project Button - ONLY for Admin/SuperAdmin */}
          {isAdminOrSuperAdmin && (
            <Button
              variant="contained"
              startIcon={<Icon name="plus2" />}
              onClick={handleAddProjectClick}
              sx={{
                height: 32,
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                ...TYPOGRAPHY.H4,
                px: 2,
                "&:hover": { bgcolor: COLORS.SECONDARY },
              }}
            >
              Add Project
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Message */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table - Responsive Container */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "transparent",
          overflow: "auto",
          width: "100%"
        }}
      >
        <TableContainer>
          <Table
            sx={{
              backgroundColor: "transparent",
              minWidth: isMobile ? 500 : "auto",
              "& thead th": {
                backgroundColor: "transparent",
              },
              "& tbody td": {
                backgroundColor: "transparent",
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {getVisibleHeaders().map((header) => (
                  <TableCell
                    key={header.key}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.PRIMARY,
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      whiteSpace: "nowrap",
                      py: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <span>{header.key}</span>
                      {header.sortable && (
                        <CustomTooltip title={
                          sortConfig.field === header.field
                            ? `Sorted ${sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}`
                            : `Sort by ${header.key}`
                        }>
                          <IconButton
                            onClick={() => handleSort(header.field)}
                            sx={{
                              padding: 0,
                              width: 24,
                              height: 24,
                            }}
                          >
                            <Icon
                              name={getSortIcon(header.field)}
                              color={
                                sortConfig.field === header.field
                                  ? COLORS.ERROR
                                  : COLORS.LIGHT_SECONDARY
                              }
                            />
                          </IconButton>
                        </CustomTooltip>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={getVisibleHeaders().length} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={getVisibleHeaders().length} align="center" sx={{ py: 3 }}>
                    <Typography sx={{ color: COLORS.TEXT }}>
                      {isEngineerRole ? "No projects assigned to you" : "No projects found"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                projects.map((project) => (
                  <TableRow
                    key={project._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      }
                    }}
                  >
                    {getVisibleHeaders().map((header) => renderTableCell(header, project))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && projects.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              p: 2,
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#777" }}>
              {startIndex} - {endIndex} of {totalProjects}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomTooltip title="Previous">
                <span>
                  <IconButton
                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                    disabled={page === 1}
                    sx={{
                      width: 32,
                      height: 32,
                      color: page === 1 ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
                    }}
                  >
                    <Icon name="chevron-left" />
                  </IconButton>
                </span>
              </CustomTooltip>

              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let pageNum;
                if (totalPages <= 3) {
                  pageNum = i + 1;
                } else if (page === 1) {
                  pageNum = i + 1;
                } else if (page === totalPages) {
                  pageNum = totalPages - 2 + i;
                } else {
                  pageNum = page - 1 + i;
                }

                if (pageNum > 0 && pageNum <= totalPages) {
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      sx={{
                        minWidth: 32,
                        height: 32,
                        fontSize: 12,
                        fontWeight: 500,
                        bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                        color: page === pageNum ? "#fff" : COLORS.TEXT,
                        border: page === pageNum
                          ? "none"
                          : `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        borderRadius: 1,
                        "&:hover": {
                          bgcolor: page === pageNum
                            ? COLORS.PRIMARY
                            : COLORS.LIGHT_BACKGROUND,
                        },
                      }}
                    >
                      {pageNum}
                    </Button>
                  );
                }
                return null;
              })}

              <CustomTooltip title="Next">
                <span>
                  <IconButton
                    onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={page === totalPages}
                    sx={{
                      width: 32,
                      height: 32,
                      color: page === totalPages ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
                    }}
                  >
                    <Icon name="chevron-right" />
                  </IconButton>
                </span>
              </CustomTooltip>
            </Box>
          </Box>
        )}
      </Paper>

      {/* View Menu */}
      <Menu
        anchorEl={viewAnchorEl}
        open={Boolean(viewAnchorEl)}
        onClose={() => setViewAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
            minWidth: 160,
          },
        }}
      >
        {[
          { label: "All", value: "ALL", icon: "usersGroup", color: COLORS.PRIMARY },
          { label: "Active", value: "ACTIVE", icon: "users", color: "#8CD47E" },
          { label: "Completed", value: "COMPLETED", icon: "users", color: "#4CAF50" },
          { label: "On Hold", value: "ON_HOLD", icon: "users", color: "#FF9800" },
          { label: "Cancelled", value: "CANCELLED", icon: "users", color: "#FF6961" },
        ].map((item) => (
          <MenuItem
            key={item.value}
            onClick={() => handleViewFilter(item.value)}
            sx={{
              gap: 1.5,
              borderRadius: 1,
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
              color: item.color,
            }}
          >
            <Icon name={item.icon} color={item.color} />
            <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Global Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
            minWidth: 160,
          },
        }}
      >
        {[
          { label: "Latest First", value: "LATEST", icon: "foldUp" },
          { label: "Oldest First", value: "OLDEST", icon: "foldDown" },
          { label: "A to Z", value: "AZ", icon: "foldUp" },
          { label: "Z to A", value: "ZA", icon: "foldDown" },
        ].map((item) => (
          <MenuItem
            key={item.value}
            onClick={() => handleGlobalSort(item.value)}
            sx={{
              gap: 1.5,
              borderRadius: 1,
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
              color: COLORS.TEXT,
            }}
          >
            <Icon name={item.icon} color={COLORS.TEXT} />
            <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Assign Engineer Modal - Only for Admin/SuperAdmin */}
      {isAdminOrSuperAdmin && selectedProject && (
        <AssignEngineerModal
          open={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
          onAssign={handleAssignmentSuccess}
          showSnackbar={showSnackbar}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Project"
        content={`Are you sure you want to delete "${projectToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setProjectToDelete(null);
        }}
      />
    </Box>
  );
};

export default InHouseProjects;